import { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'next-themes';
import { Elements } from '@stripe/react-stripe-js';
import { Loader2 } from 'lucide-react';
import { getStripe, isStripeConfigured } from '@/lib/stripe';
import { CardCapture, buildAppearance } from '@/components/billing/card-capture';
import { Button } from '@/components/ui/button';
import {
  resolveCardLink,
  mintCardLinkSetupIntent,
  buildCardLinkApi,
  type CardLinkResolveResult,
} from './public-card-api';

type Resolved = Extract<CardLinkResolveResult, { done: false }>;
type View = 'loading' | 'form' | 'success';

/**
 * AIT-505 Task 8 — /card, the public (account-less) add-a-card link. An org
 * admin's paused/trialing customer gets this URL (never the app URL) to save
 * a card without a WorkOS session. The token travels in the URL FRAGMENT
 * (`#crd_...`), never a query param, so browsers never send it to a server in
 * a network request or a Referer header. The fragment deliberately STAYS in
 * the address bar (operator decision 2026-08-29): it's a permanent bearer
 * link the customer already holds in WhatsApp/email, so refresh and
 * bookmarks must keep working. Telemetry protection lives in the referrer
 * being disabled here (`<meta name="referrer">`), PostHog dropping URL hashes
 * (`disable_capture_url_hashes` in main.tsx), and Sentry stripping fragments
 * (lib/sentry.ts + lib/strip-card-token.ts).
 */
export function CardLinkPage() {
  const { resolvedTheme } = useTheme();
  const calledRef = useRef(false);
  const savingRef = useRef(false);
  // Pasting a second link while the first resolve is still in flight must not
  // let whichever response lands last win (review P2) — bumped before each
  // fetch, checked after it resolves, so a stale response is dropped instead
  // of overwriting the newer intake's state.
  const intakeSeqRef = useRef(0);

  const [view, setView] = useState<View>('loading');
  // Read once from window.location.hash on mount and never again (refs can't
  // be read during render — @react-compiler — so the token lives in state).
  const [token, setToken] = useState<string | null>(null);
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [mintError, setMintError] = useState<string | null>(null);
  const [chargeFailedError, setChargeFailedError] = useState<string | null>(null);

  // no-referrer for the lifetime of this page only — an outbound link/asset
  // request from /card must never carry the card-link token in a Referer
  // header. Not global: every other page keeps the app's default referrer policy.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    // Reads the fragment and resolves. The fragment STAYS in the address bar
    // (operator decision 2026-08-29): the link is a permanent bearer URL the
    // customer already holds in WhatsApp/email, so hiding it from the bar
    // bought nothing and broke refresh/bookmarks. The fragment is never sent
    // to any server in a URL (browsers strip fragments from requests), the
    // page is no-referrer, PostHog drops URL hashes, and Sentry strips
    // fragments — telemetry protection lives there, not in URL scrubbing.
    // Runs on mount AND on `hashchange`: pasting the full link while already
    // sitting on /card is a same-document navigation — the browser never
    // reloads, so without the listener the page would keep showing its old
    // state while the address bar carries a fresh token.
    const intake = () => {
      const readToken = window.location.hash.slice(1);
      setToken(readToken || null);

      // Bare /card with no fragment (a hand-typed URL or truncated link) —
      // nothing to resolve here, and no screen worth showing: behave like any
      // unknown address and land on the app (operator call 2026-08-29).
      if (!readToken) {
        window.location.replace('/');
        return;
      }

      const seq = ++intakeSeqRef.current;
      setView('loading');
      void (async () => {
        const result = await resolveCardLink(readToken);
        // A newer hash change fired while this resolve was in flight — the
        // newer intake owns the screen now, so a stale response here must
        // never apply its state (review P2).
        if (seq !== intakeSeqRef.current) return;
        if (!result.ok) {
          // Unknown/revoked link: no bespoke dead-end screens (operator call
          // 2026-08-29) — land on the app like any other address.
          window.location.replace('/');
          return;
        }
        if (result.data.done) {
          window.location.replace(result.data.plansUrl);
          return;
        }
        setResolved(result.data);
        setPhoneVerified(result.data.phoneVerified);
        setView('form');
      })();
    };

    if (!calledRef.current) {
      calledRef.current = true;
      intake();
    }
    window.addEventListener('hashchange', intake);
    return () => window.removeEventListener('hashchange', intake);
  }, []);

  const mint = async () => {
    if (!token) return;
    // Codex round 6 (P2) — same fencing as the resolve intake above: a stale
    // first-link mint response landing after a second link was pasted must
    // not overwrite the state a newer intake already owns. Capture the
    // current intake's sequence number before the request goes out; drop the
    // response if a newer intake incremented it while this one was in flight.
    const seq = intakeSeqRef.current;
    setMintError(null);
    setClientSecret(null);
    const result = await mintCardLinkSetupIntent(token);
    if (seq !== intakeSeqRef.current) return;
    if (!result.ok) {
      // Completed elsewhere between resolve and mint (another tab, a retry
      // race) — nothing left for this page to do.
      if (result.done) {
        setView('success');
        return;
      }
      setMintError(result.message);
      return;
    }
    setClientSecret(result.clientSecret);
    // AIT-505 P1 (Codex round 8) — the mint response is the authoritative
    // quote: a plan resolved fresh at mint time (after usage crossed an
    // eligibility threshold since resolve()) must win over the earlier
    // resolve()-quoted plan, so the notice below always matches what was
    // just frozen onto the SetupIntent's metadata. Mint returns `plan: null`
    // for trialing/active (no immediate charge bound) — that is NOT a more
    // authoritative answer than resolve()'s own quote, so only a truthy
    // mint plan overwrites the resolved one.
    if (result.plan) {
      const mintedPlan = result.plan;
      setResolved((prev) => (prev ? { ...prev, plan: mintedPlan } : prev));
    }
  };

  // mint intentionally omitted from deps: it's a plain closure re-created
  // every render, not a value this effect should re-run for — it must fire
  // once, on the transition into 'form', never again on a `mint` identity
  // change (mirrors AddCardDialog's mint-on-open effect).
  useEffect(() => {
    if (view !== 'form' || !isStripeConfigured()) return;
    void mint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // token is read once by the mount effect above and is set before `view`
  // ever becomes 'form' (the only place `api` is used), so this is never
  // built with a null token in practice.
  const api = useMemo(() => (token ? buildCardLinkApi(token) : null), [token]);

  // Brand mark only (operator call 2026-08-29) — a no-login payment page
  // without the brand reads as phishing; the alt text still names it.
  const brand = (
    <div className="flex items-center justify-center pb-2">
      <img src="/logo-mark.png" alt="HookMyApp" className="h-14 w-14" />
    </div>
  );

  if (view === 'loading') {
    return (
      <div role="status" className="flex items-center justify-center min-h-screen">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  if (view === 'success') {
    return (
      <div className="max-w-md mx-auto px-4 py-8 flex flex-col items-center gap-3 text-center">
        {brand}
        <h1 className="text-xl font-semibold">You&apos;re all set</h1>
        <p className="text-sm text-muted-foreground">
          Your card was saved for {resolved?.orgName}. You can close this tab now.
        </p>
      </div>
    );
  }

  if (!resolved) return null;

  const notice = buildNotice(resolved);

  return (
    <div className="max-w-md mx-auto px-4 py-8 flex flex-col gap-4">
      {brand}
      <h1 className="text-xl font-semibold">Add a credit card for {resolved.orgName}</h1>
      {notice && <p className="text-sm text-muted-foreground">{notice}</p>}
      {chargeFailedError && (
        <p role="alert" className="text-sm text-destructive">
          {chargeFailedError}
        </p>
      )}
      {!isStripeConfigured() && (
        <p role="alert" className="text-sm text-destructive">
          Card entry is unavailable right now. Please contact support.
        </p>
      )}
      {isStripeConfigured() && mintError && (
        <div className="flex flex-col gap-3">
          <p role="alert" className="text-sm text-destructive">
            {mintError}
          </p>
          <Button onClick={mint}>Retry</Button>
        </div>
      )}
      {isStripeConfigured() && !mintError && !clientSecret && (
        <div role="status" className="flex items-center justify-center py-6">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading</span>
        </div>
      )}
      {clientSecret && api && (
        <Elements
          key={clientSecret}
          stripe={getStripe()}
          options={{
            clientSecret,
            appearance: buildAppearance(resolvedTheme === 'dark'),
            fonts: [{ cssSrc: `${window.location.origin}/fonts/geist-stripe.css` }],
          }}
        >
          <CardCapture
            api={api}
            needsPhone={!phoneVerified}
            phoneStatusPending={false}
            savingRef={savingRef}
            onSaved={() => {}}
            onVerified={() => setPhoneVerified(true)}
            onClose={() => setView('success')}
            onOutcome={(outcome) => {
              if (outcome === 'charge_failed') {
                setChargeFailedError('Your card was saved, but the charge failed. Try another card.');
                // Codex round 7 (P2) — this page has no dialog close guard,
                // but savingRef is the same shared contract CardCapture
                // writes to; keep it honest here too before the remint
                // (which re-keys <Elements> and unmounts CardCapture) can
                // strand it at true.
                savingRef.current = false;
                void mint();
              } else {
                setChargeFailedError(null);
              }
            }}
          />
        </Elements>
      )}
    </div>
  );
}

/** Review round 3 fix (P2) — `Math.round(cents / 100)` drops cents, so an
 *  invoice-derived amount like $87.34 rendered as $87. Whole-dollar amounts
 *  (the common catalog-price case) stay decimal-free; anything with cents
 *  (invoice prorations/credits/tax) shows both digits. */
function formatUsd(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

/** State-dependent notice — mirrors the authenticated Plans page's
 *  `addCardNotice` (components/billing/plans-section.tsx), adapted to the
 *  public wire's flatter `plan` shape (no pendingCardPlan/resumePreview
 *  distinction — the server already picked the one plan this token resumes
 *  onto or converts to). */
function buildNotice(resolved: Resolved): string | undefined {
  const { status, plan, trialEndsAt, orgName } = resolved;
  if (status === 'active') {
    return `Your card will be saved for ${orgName}. Your next bill will use this card.`;
  }
  if (!plan) return undefined;
  if (status === 'paused') {
    return `Your subscription is paused. Saving a card resumes ${plan.name} and charges ${formatUsd(plan.priceCents)} now.`;
  }
  // trialing — byte-identical to the authenticated modal's no-pick copy
  // (plans-section.tsx addCardNotice): the customer opening this link never
  // picked a plan, so the usage-based wording is the truthful variant.
  const trialEndLabel = trialEndsAt
    ? new Date(trialEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
    : null;
  const priceLabel = `${formatUsd(plan.priceCents)}${plan.interval === 'annual' ? '/year' : '/month'}`;
  const earnedPart = `, which is currently ${plan.name} at ${priceLabel},`;
  return trialEndLabel
    ? `When your trial ends on ${trialEndLabel}, you will be charged for the relevant plan based on your usage${earnedPart} unless you choose otherwise.`
    : `When your trial ends, you will be charged for the relevant plan based on your usage${earnedPart} unless you choose otherwise.`;
}
